/**
 * DOM tree navigation extensions — port 1:1 din legacy common.js lines 379-552.
 *   - Node.prototype.find / findChildren / index / siblings / next / prev / parents
 *   - NodeList.prototype.filter
 */

import { isset } from './util.js';

Node.prototype.find = function (this: Node, selector: string, list?: true | ((el: Element) => boolean)) {
    const el = this as Element;
    if (list === true) return el.querySelectorAll(selector);
    if (typeof list === 'function') {
        return [...el.querySelectorAll(selector)].filter(list);
    }
    return el.querySelector(selector);
} as Node['find'];

Node.prototype.findChildren = function (this: Node, selector?: string, list?: true | ((child: Element, i: number) => boolean)) {
    const children = Array.from((this as Element).children) as Element[];

    if (list === true) {
        if (selector) {
            return children.filter((c) => c.matches(selector));
        }
        return children;
    }
    if (typeof list === 'function') {
        return children.filter((child, i) => {
            if (!selector || child.matches(selector)) {
                return list(child, i);
            }
            return false;
        });
    }
    if (selector) {
        return children.find((c) => c.matches(selector)) ?? null;
    }
    return ((this as Element).children[0] as Element | undefined) ?? null;
} as Node['findChildren'];

Node.prototype.index = function (this: Node, nodes: HTMLCollection | NodeListOf<Element>) {
    return [...nodes].indexOf(this as Element);
};

Node.prototype.siblings = function (this: Node, selector?: string, list?: true | ((el: Element) => boolean)) {
    const sibs: Element[] = [];
    let elem = this.parentNode?.firstChild as Element | null;
    if (!elem) return sibs;

    if (typeof list === 'function') {
        while ((elem = elem.nextElementSibling as Element | null)) {
            if (elem !== this && elem.nodeType === 1 && (!selector || elem.matches(selector))) {
                if (list(elem)) sibs.push(elem);
            }
        }
    } else {
        while ((elem = elem.nextElementSibling as Element | null)) {
            if (elem !== this && elem.nodeType === 1 && (!selector || elem.matches(selector))) {
                sibs.push(elem);
            }
        }
    }
    return sibs;
} as Node['siblings'];

Node.prototype.next = function (this: Node, selector?: string) {
    const node = (this as Element).nextElementSibling;
    if (node && selector && !node.matches(selector)) return null;
    return node;
} as Node['next'];

Node.prototype.prev = function (this: Node, selector?: string) {
    const node = (this as Element).previousElementSibling;
    if (node && selector && !node.matches(selector)) return null;
    return node;
} as Node['prev'];

Node.prototype.parents = function (this: Node, selector?: string, list?: boolean) {
    const parents: Element[] = [];
    let parent = (this as Element).parentElement;

    while (parent) {
        if (!selector || parent.matches(selector)) {
            if (!isset(list)) {
                // Forma 1 — primul match
                return parent;
            }
            if (list === true) {
                parents.push(parent);
            }
        }
        parent = parent.parentElement;
    }
    return list ? parents : null;
} as Node['parents'];

NodeList.prototype.filter = function (this: NodeList, selector: string, list?: true | ((el: Element) => boolean)) {
    if (list === true) {
        return [...this].filter((item) => (item as Element).matches(selector)) as Element[];
    }
    if (typeof list === 'function') {
        return [...this].filter((item) => {
            const el = item as Element;
            if (el.matches(selector)) return list(el);
            return false;
        }) as Element[];
    }
    return ([...this].find((node) => (node as Element).matches(selector)) ?? null) as Element | null;
} as NodeList['filter'];
